/**
 * Operator Tasks Management V2 - Admin View
 * 
 * Multi-tier payment rules with conditional logic
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/ui/PageHeader';
import API_BASE from '@/lib/apiBase';
import { apiFetch } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Plus,
  Edit,
  Copy,
  Trash2,
  Calendar,
  DollarSign,
  Users,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
  Settings,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface RuleTier {
  id: string;
  condition: 'equals' | 'range' | 'greater_than' | 'less_than' | 'greater_equal' | 'less_equal';
  campaignMin?: number;
  campaignMax?: number;
  rate: number;
}

interface OperatorTaskRule {
  id: number;
  name: string;
  description: string;
  taskId: number;
  taskName: string;
  operatorId: number | null; // DEPRECATED
  operatorName: string | null; // DEPRECATED
  isBaseTemplate: boolean; // DEPRECATED
  applicableOperatorIds: number[] | null; // NEW: NULL = all operators, array = specific operators
  applicableOperatorNames?: string[]; // For display
  campaignCountScope: 'per_store' | 'per_cycle';
  tiers: RuleTier[];
  priority: number;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
}

interface Operator {
  id: number;
  name: string;
  username: string;
  role: string;
}

const AdminOperatorTasksV2: React.FC = () => {
  const { toast } = useToast();
  const [rules, setRules] = useState<OperatorTaskRule[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<OperatorTaskRule | null>(null);
  const [showInactiveRules, setShowInactiveRules] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    taskId: '',
    operatorId: '', // DEPRECATED
    applicableOperatorIds: null as number[] | null, // NEW: NULL = all, array = specific operators
    campaignCountScope: 'per_store', // 'per_store' or 'per_cycle'
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: '',
    isActive: true,
  });

  // Operator selection state
  const [operatorSelectionMode, setOperatorSelectionMode] = useState<'all' | 'specific'>('all');
  const [selectedOperators, setSelectedOperators] = useState<string[]>([]);

  const [tiers, setTiers] = useState<RuleTier[]>([
    { id: '1', condition: 'greater_equal', campaignMin: 1, rate: 0 }
  ]);

  // Fetch operators once on mount
  useEffect(() => {
    fetchOperators();
  }, []);

  // Fetch rules when filters change
  useEffect(() => {
    fetchRules();
  }, [selectedOperator, showInactiveRules]);

  // Fetch base tasks for dropdown
  const { data: baseTasks = [], isLoading: baseTasksLoading } = useQuery<Array<{ id: number; name: string; description?: string; is_active?: boolean }>>({
    queryKey: ['/api/tasks'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/tasks`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to load tasks');
      }
      const data = await response.json();
      return data.tasks || data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Filter to active tasks only, excluding auto-generated tasks
  const activeTasks = baseTasks.filter(task =>
    task.is_active !== false &&
    !task.name?.includes('(auto)')
  );

  const fetchOperators = async () => {
    try {
      // Use apiFetch for proper Supabase auth token
      const response = await apiFetch('/api/operator-tasks/operators');

      if (!response.ok) {
        throw new Error('Failed to fetch operators');
      }

      const data = await response.json();
      setOperators(data);
    } catch (error) {
      console.error('Error fetching operators:', error);
      toast({
        title: 'Error',
        description: 'Failed to load operators',
        variant: 'destructive',
      });
    }
  };

  const fetchRules = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Add includeInactive parameter
      if (showInactiveRules) {
        params.append('includeInactive', 'true');
      }

      // Fetch all rules
      const response = await fetch(`${API_BASE}/api/operator-tasks?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch operator tasks');
      }

      const data = await response.json();

      // Transform API response to match our interface
      const transformedRules = data.map((rule: any) => ({
        id: rule.id,
        name: rule.name,
        description: rule.description,
        taskId: rule.task_id,
        taskName: rule.task_name,
        operatorId: rule.operator_id,
        operatorName: rule.operator_name,
        isBaseTemplate: rule.is_base_template,
        applicableOperatorIds: rule.applicable_operator_ids,
        applicableOperatorNames: rule.applicable_operator_names,
        tiers: rule.tiers.map((tier: any) => ({
          id: tier.id.toString(),
          condition: tier.condition_type,
          campaignMin: tier.campaign_min,
          campaignMax: tier.campaign_max,
          rate: parseFloat(tier.rate),
        })),
        priority: rule.priority,
        isActive: rule.is_active,
        effectiveFrom: rule.effective_from?.split('T')[0] || '',
        effectiveTo: rule.effective_to?.split('T')[0] || null,
        campaignCountScope: rule.campaign_count_scope || 'per_store',
      }));

      setRules(transformedRules);
    } catch (error) {
      console.error('Error fetching operator tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to load operator tasks',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter rules by selected operator
  const filteredRules = rules.filter(r => {
    if (selectedOperator === 'all') return true;
    if (!r.applicableOperatorIds || r.applicableOperatorIds.length === 0) return false;
    return r.applicableOperatorIds.some(id => id.toString() === selectedOperator);
  });

  const addTier = () => {
    setTiers([...tiers, {
      id: Date.now().toString(),
      condition: 'greater_equal',
      campaignMin: 1,
      rate: 0
    }]);
  };

  const removeTier = (id: string) => {
    if (tiers.length > 1) {
      setTiers(tiers.filter(t => t.id !== id));
    }
  };

  const updateTier = (id: string, updates: Partial<RuleTier>) => {
    setTiers(tiers.map(t => t.id === id ? { ...t, ...updates } : t));
  };

  const handleCreate = async () => {
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        taskId: parseInt(formData.taskId),
        operatorId: formData.operatorId ? parseInt(formData.operatorId) : null, // DEPRECATED - for backward compat
        applicableOperatorIds: formData.applicableOperatorIds, // NEW: multi-operator support
        // FIX: Set isBaseTemplate based on operator selection mode
        isBaseTemplate: operatorSelectionMode === 'all',
        campaignCountScope: formData.campaignCountScope,
        effectiveFrom: formData.effectiveFrom,
        effectiveTo: formData.effectiveTo || null,
        isActive: formData.isActive,
        priority: 1,
        tiers: tiers.map((tier, index) => ({
          tierOrder: index + 1,
          conditionType: tier.condition,
          campaignMin: tier.campaignMin || null,
          campaignMax: tier.campaignMax || null,
          rate: tier.rate,
        })),
      };

      console.log('[CREATE RULE] Sending payload:', payload);

      const response = await fetch(`${API_BASE}/api/operator-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[CREATE RULE] Server error:', errorData);
        throw new Error(errorData.error || 'Failed to create operator task');
      }

      toast({
        title: 'Success',
        description: 'Operator task created successfully',
      });

      setShowCreateDialog(false);
      resetForm();
      fetchRules();
    } catch (error: any) {
      console.error('Error creating operator task:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create operator task',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (rule: OperatorTaskRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description,
      taskId: rule.taskId.toString(),
      operatorId: rule.operatorId?.toString() || '', // DEPRECATED
      applicableOperatorIds: rule.applicableOperatorIds, // NEW
      campaignCountScope: (rule as any).campaignCountScope || 'per_store',
      effectiveFrom: rule.effectiveFrom,
      effectiveTo: rule.effectiveTo || '',
      isActive: rule.isActive,
    });

    // Set operator selection mode
    if (rule.applicableOperatorIds === null) {
      setOperatorSelectionMode('all');
      setSelectedOperators([]);
    } else {
      setOperatorSelectionMode('specific');
      setSelectedOperators(rule.applicableOperatorIds.map(id => id.toString()));
    }

    setTiers(rule.tiers);
    setShowEditDialog(true);
  };

  const handleCopy = (rule: OperatorTaskRule) => {
    console.log('[COPY] Copying rule:', rule.id);

    // Pre-fill the create form with the rule's data
    setFormData({
      name: `${rule.name} (Copy)`,
      description: rule.description,
      taskId: rule.taskId.toString(),
      operatorId: rule.operatorId?.toString() || '', // DEPRECATED
      applicableOperatorIds: rule.applicableOperatorIds, // NEW
      campaignCountScope: (rule as any).campaignCountScope || 'per_store',
      effectiveFrom: new Date().toISOString().split('T')[0], // Set to today
      effectiveTo: rule.effectiveTo || '',
      isActive: true, // Default to active
    });

    // Set operator selection mode
    if (rule.applicableOperatorIds === null) {
      setOperatorSelectionMode('all');
      setSelectedOperators([]);
    } else {
      setOperatorSelectionMode('specific');
      setSelectedOperators(rule.applicableOperatorIds.map(id => id.toString()));
    }

    // Copy tiers
    setTiers(rule.tiers);

    // Open create dialog (not edit)
    setShowCreateDialog(true);

    toast({
      title: 'Rule Copied',
      description: 'You can now modify and save the copied rule',
    });
  };

  const handleUpdate = async () => {
    if (!editingRule) return;

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        applicableOperatorIds: formData.applicableOperatorIds, // NEW: multi-operator support
        // FIX: Include isBaseTemplate in update payload
        isBaseTemplate: operatorSelectionMode === 'all',
        campaignCountScope: formData.campaignCountScope,
        effectiveFrom: formData.effectiveFrom,
        effectiveTo: formData.effectiveTo || null,
        isActive: formData.isActive,
        priority: 1,
        tiers: tiers.map((tier, index) => ({
          tierOrder: index + 1,
          conditionType: tier.condition,
          campaignMin: tier.campaignMin || null,
          campaignMax: tier.campaignMax || null,
          rate: tier.rate,
        })),
      };

      const response = await fetch(`${API_BASE}/api/operator-tasks/${editingRule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update operator task');
      }

      toast({
        title: 'Success',
        description: 'Operator task updated successfully',
      });

      setShowEditDialog(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error('Error updating operator task:', error);
      toast({
        title: 'Error',
        description: 'Failed to update operator task',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (ruleId: number) => {
    console.log('[DELETE] Function called for rule ID:', ruleId);

    if (!confirm(`Are you sure you want to delete rule ID ${ruleId}?`)) {
      console.log('[DELETE] User cancelled deletion');
      return;
    }

    console.log('[DELETE] User confirmed, proceeding with deletion...');

    try {
      console.log('[DELETE] Attempting to delete rule:', ruleId);
      const url = `${API_BASE}/api/operator-tasks/${ruleId}`;
      console.log('[DELETE] Fetching URL:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
      });

      console.log('[DELETE] Response status:', response.status);
      const data = await response.json();
      console.log('[DELETE] Response data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete operator task');
      }

      toast({
        title: 'Success',
        description: 'Operator task deleted successfully',
      });

      fetchRules();
    } catch (error: any) {
      console.error('[DELETE] Error deleting operator task:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete operator task',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      taskId: '',
      operatorId: '',
      applicableOperatorIds: null,
      campaignCountScope: 'per_store',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: '',
      isActive: true,
    });
    setOperatorSelectionMode('all');
    setSelectedOperators([]);
    setTiers([{ id: '1', condition: 'greater_equal', campaignMin: 1, rate: 0 }]);
  };

  const getConditionLabel = (tier: RuleTier) => {
    const minValue = tier.campaignMin ?? 'N/A';
    const maxValue = tier.campaignMax ?? 'N/A';
    switch (tier.condition) {
      case 'equals':
        return `= ${minValue}`;
      case 'range':
        return `${minValue} - ${maxValue}`;
      case 'greater_than':
        return `> ${minValue}`;
      case 'less_than':
        return `< ${minValue}`;
      case 'greater_equal':
        return `≥ ${minValue}`;
      case 'less_equal':
        return `≤ ${minValue}`;
      default:
        return '';
    }
  };

  // IMPORTANT: This is NOT a component - it's a const JSX element to prevent re-creation on render
  const tierFormContent = (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/20">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Payment Tiers</Label>
        <Button size="sm" variant="outline" onClick={addTier}>
          <Plus className="h-4 w-4 mr-2" />
          Add Tier
        </Button>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Define multiple tiers based on campaigns per unique store. The system will apply the first matching tier. Campaign counts are calculated per store (number of campaigns per unique store), not stores per campaign.
        </AlertDescription>
      </Alert>

      {tiers.map((tier, index) => (
        <div key={tier.id} className="border rounded-lg p-4 space-y-3 bg-background">
          <div className="flex items-center justify-between">
            <Label className="font-medium">Tier {index + 1}</Label>
            {tiers.length > 1 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeTier(tier.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Condition</Label>
              <Select
                value={tier.condition}
                onValueChange={(v) => updateTier(tier.id, { condition: v as RuleTier['condition'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equals">Equals (=)</SelectItem>
                  <SelectItem value="range">Range (between)</SelectItem>
                  <SelectItem value="greater_equal">Greater or Equal (≥)</SelectItem>
                  <SelectItem value="less_equal">Less or Equal (≤)</SelectItem>
                  <SelectItem value="greater_than">Greater Than (&gt;)</SelectItem>
                  <SelectItem value="less_than">Less Than (&lt;)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(tier.condition === 'equals' || tier.condition === 'greater_than' ||
              tier.condition === 'greater_equal' || tier.condition === 'less_than' ||
              tier.condition === 'less_equal' || tier.condition === 'range') && (
                <div className="space-y-2">
                  <Label>Campaigns per Store {tier.condition === 'range' ? '(Min)' : ''}</Label>
                  <Input
                    type="number"
                    value={tier.campaignMin ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateTier(tier.id, { campaignMin: val === '' ? undefined : parseInt(val) });
                    }}
                    placeholder="1"
                  />
                  <p className="text-xs text-muted-foreground">Number of campaigns per unique store</p>
                </div>
              )}

            {tier.condition === 'range' && (
              <div className="space-y-2">
                <Label>Campaigns per Store (Max)</Label>
                <Input
                  type="number"
                  value={tier.campaignMax ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateTier(tier.id, { campaignMax: val === '' ? undefined : parseInt(val) });
                  }}
                  placeholder="5"
                />
                <p className="text-xs text-muted-foreground">Number of campaigns per unique store</p>
              </div>
            )}

            {tier.condition !== 'range' && (
              <div className="space-y-2">
                <Label>Rate (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={tier.rate ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateTier(tier.id, { rate: val === '' ? 0 : parseFloat(val) });
                  }}
                  placeholder="0.00"
                />
              </div>
            )}

            {tier.condition === 'range' && (
              <div className="space-y-2 col-start-2">
                <Label>Rate (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={tier.rate ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateTier(tier.id, { rate: val === '' ? 0 : parseFloat(val) });
                  }}
                  placeholder="0.00"
                />
              </div>
            )}
          </div>

          <div className="text-sm text-muted-foreground bg-muted/30 p-2 rounded">
            IF campaigns per store {getConditionLabel(tier)} THEN €{tier.rate.toFixed(2)} per task
          </div>
        </div>
      ))}
    </div>
  );

  // IMPORTANT: This is NOT a component - it's a const JSX element to prevent re-creation on render
  const ruleFormContent = (
    <div className="space-y-4">
      {/* Basic Info */}
      <div className="space-y-2">
        <Label htmlFor="name">Rule Name *</Label>
        <Input
          id="name"
          placeholder="e.g., Standard Campaign Volume Discount"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Describe when this rule applies..."
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={2}
        />
      </div>

      {/* Operator Selection - NEW MULTI-SELECT */}
      <div className="space-y-3 border-t pt-4">
        <Label className="text-base font-semibold">Apply Rule To *</Label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="allOperators"
              name="operatorSelection"
              aria-label="Apply rule to all operators"
              checked={operatorSelectionMode === 'all'}
              onChange={() => {
                setOperatorSelectionMode('all');
                setSelectedOperators([]);
                setFormData({ ...formData, applicableOperatorIds: null });
              }}
              className="h-4 w-4"
            />
            <Label htmlFor="allOperators" className="font-normal cursor-pointer">
              All Operators (Base Template)
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="specificOperators"
              name="operatorSelection"
              aria-label="Apply rule to specific operators"
              checked={operatorSelectionMode === 'specific'}
              onChange={() => {
                setOperatorSelectionMode('specific');
                setFormData({ ...formData, applicableOperatorIds: [] });
              }}
              className="h-4 w-4"
            />
            <Label htmlFor="specificOperators" className="font-normal cursor-pointer">
              Specific Operators
            </Label>
          </div>

          {operatorSelectionMode === 'specific' && (
            <div className="ml-6 space-y-2">
              <Label htmlFor="operatorSelect" className="text-sm">
                Select Operators *
              </Label>
              <MultiSelect
                options={operators.map(op => ({
                  value: op.id.toString(),
                  label: op.name,
                }))}
                selected={selectedOperators}
                onChange={(newSelection) => {
                  setSelectedOperators(newSelection);
                  setFormData({
                    ...formData,
                    applicableOperatorIds: newSelection.map(id => parseInt(id))
                  });
                }}
                placeholder="Select operators..."
              />
              {selectedOperators.length === 0 && (
                <Alert className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please select at least one operator for this rule
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 border-t pt-4">
        <div className="space-y-2">
          <Label htmlFor="taskId">Task *</Label>
          <Select
            value={formData.taskId}
            onValueChange={(v) => setFormData({ ...formData, taskId: v })}
            disabled={baseTasksLoading}
          >
            <SelectTrigger id="taskId">
              <SelectValue placeholder={baseTasksLoading ? "Loading tasks..." : "Select task"} />
            </SelectTrigger>
            <SelectContent>
              {activeTasks.length === 0 ? (
                <SelectItem value="__none" disabled>
                  {baseTasksLoading ? 'Loading...' : 'No tasks available'}
                </SelectItem>
              ) : (
                activeTasks.map((task) => (
                  <SelectItem key={task.id} value={task.id.toString()}>
                    {task.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Campaign Counting Scope */}
      <div className="space-y-2 border rounded-lg p-4 bg-muted/10">
        <Label htmlFor="campaignCountScope" className="text-base font-semibold">Campaign Counting Scope *</Label>
        <Select
          value={formData.campaignCountScope}
          onValueChange={(v) => setFormData({ ...formData, campaignCountScope: v })}
        >
          <SelectTrigger id="campaignCountScope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="per_store">Per Store (count campaigns at each store)</SelectItem>
            <SelectItem value="per_cycle">Per Cycle (count total campaigns in cycle)</SelectItem>
          </SelectContent>
        </Select>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Per Store:</strong> If operator works on 3 campaigns at Store A, they get the 3-campaign rate for Store A's tasks. Campaign count is calculated per unique store (number of campaigns per store), not stores per campaign.
            <br />
            <strong>Per Cycle:</strong> If operator works on 5 campaigns total across all stores, they get the 5-campaign rate for all tasks.
          </AlertDescription>
        </Alert>
      </div>

      {/* Payment Tiers */}
      {tierFormContent}

      {/* Temporal Validity */}
      <div className="space-y-2 border-t pt-4">
        <Label className="text-base font-semibold">Effective Dates</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="effectiveFrom">Effective From *</Label>
            <Input
              id="effectiveFrom"
              type="date"
              value={formData.effectiveFrom}
              onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="effectiveTo">Effective To</Label>
            <Input
              id="effectiveTo"
              type="date"
              value={formData.effectiveTo}
              onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">Leave blank for no end date</p>
          </div>
        </div>
      </div>

      {/* Active Status */}
      <div className="flex items-center space-x-2 border-t pt-4">
        <Switch
          id="isActive"
          checked={formData.isActive}
          onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
          className="data-[state=checked]:bg-green-600"
        />
        <Label htmlFor="isActive" className={formData.isActive ? "text-green-600 font-medium" : ""}>
          {formData.isActive ? "Active Rule" : "Inactive Rule"}
        </Label>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        icon={Settings}
        title="Operator Payment Rules (Multi-Tier)"
        description="Manage task payment rules with conditional pricing tiers. Campaign counts are calculated per unique store (not stores per campaign)."
      >
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Rule
        </Button>
      </PageHeader>

      {/* Show Inactive Toggle */}
      <div className="flex items-center space-x-2 p-4 bg-muted/20 rounded-lg border border-border">
        <Switch
          id="showInactive"
          checked={showInactiveRules}
          onCheckedChange={setShowInactiveRules}
        />
        <Label htmlFor="showInactive" className="cursor-pointer">
          Show Inactive/Deleted Rules
        </Label>
        {showInactiveRules && (
          <Badge variant="secondary" className="ml-2">Showing all rules</Badge>
        )}
      </div>

      {/* Info Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Base Templates</strong> apply to all operators by default. <strong>Operator-Specific Rules</strong> override base templates. Each rule can have multiple pricing tiers based on campaign count.
        </AlertDescription>
      </Alert>

      {/* Unified Rules List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Payment Rules</CardTitle>
              <CardDescription>
                Multi-tier payment rules - assign different operators and rates per rule
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="operatorFilter" className="text-sm whitespace-nowrap">Filter by Operator:</Label>
              <Select value={selectedOperator} onValueChange={setSelectedOperator}>
                <SelectTrigger className="w-[200px]" id="operatorFilter">
                  <SelectValue placeholder="All Operators" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Operators</SelectItem>
                  {operators.map((operator) => (
                    <SelectItem key={operator.id} value={operator.id.toString()}>
                      {operator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading rules...</div>
          ) : filteredRules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {selectedOperator === 'all'
                ? 'No rules configured yet. Create your first rule!'
                : 'No rules found for this operator.'}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRules.map((rule) => (
                <Card key={rule.id}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-lg">{rule.name}</div>
                          <Badge variant="secondary" className="text-xs">ID: {rule.id}</Badge>
                          {rule.applicableOperatorNames && rule.applicableOperatorNames.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-4xl">
                              {rule.applicableOperatorNames.map((name, idx) => (
                                <Badge key={idx} variant="outline">{name}</Badge>
                              ))}
                            </div>
                          ) : rule.operatorName ? (
                            <Badge variant="outline">{rule.operatorName}</Badge>
                          ) : null}
                        </div>
                        <div className="text-sm text-muted-foreground">{rule.taskName}</div>
                        {rule.description && (
                          <div className="text-sm text-muted-foreground mt-1">{rule.description}</div>
                        )}
                        <div className="flex gap-2 mt-2">
                          {rule.isActive ? (
                            <Badge variant="default" className="gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                          <Badge variant="outline">Effective: {rule.effectiveFrom}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(rule)}
                          title="Edit rule"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(rule)}
                          title="Copy rule"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(rule.id)}
                          title="Delete rule"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <Label className="text-sm font-medium mb-2 block">Pricing Tiers:</Label>
                      <div className="grid gap-2">
                        {rule.tiers.map((tier, idx) => (
                          <div key={tier.id} className="flex items-center justify-between bg-muted/30 p-3 rounded-lg">
                            <div className="text-sm">
                              <span className="font-medium">Tier {idx + 1}:</span>
                              {' '}IF campaigns per store {getConditionLabel(tier)}
                            </div>
                            <div className="flex items-center gap-1 font-semibold">
                              <DollarSign className="h-3 w-3 text-muted-foreground" />
                              {tier.rate.toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
              }
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white focus:outline-none"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Create Multi-Tier Rule</DialogTitle>
            <DialogDescription>
              Define a payment rule with multiple pricing tiers based on campaigns per unique store. Campaign counts are calculated per store (number of campaigns per unique store), not stores per campaign.
            </DialogDescription>
          </DialogHeader>
          {ruleFormContent}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>
              Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white focus:outline-none"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Edit Multi-Tier Rule</DialogTitle>
            <DialogDescription>
              Modify payment rule and its pricing tiers
            </DialogDescription>
          </DialogHeader>
          {ruleFormContent}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>
              Update Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOperatorTasksV2;
